'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { cinzel } from '@/lib/fonts';
import CaseStudyStory from '@/components/our-work/CaseStudyStory';

export default function KozaPage() {
    const colorBarRefs = useRef<(HTMLDivElement | null)[]>([]);
    const colorsHeadingRef = useRef<HTMLHeadingElement>(null);
    const colorsDescRef = useRef<HTMLDivElement>(null);
    const screenshotRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('koza-bar-in');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15 }
        );
        const observer2 = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('koza-text-in');
                        observer2.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15 }
        );
        colorBarRefs.current.forEach((el) => { if (el) observer.observe(el); });
        screenshotRefs.current.forEach((el) => { if (el) observer.observe(el); });
        if (colorsHeadingRef.current) observer2.observe(colorsHeadingRef.current);
        if (colorsDescRef.current) observer2.observe(colorsDescRef.current);
        return () => { observer.disconnect(); observer2.disconnect(); };
    }, []);

    return (
        <div className={cinzel.variable}>
            <style>{`
                @keyframes koza-bar-grow {
                    from { clip-path: inset(100% 0 0 0); opacity: 0.4; }
                    to   { clip-path: inset(0% 0 0 0);   opacity: 1; }
                }
                @keyframes koza-label-fade {
                    from { opacity: 0; transform: rotate(-90deg) translateX(12px); }
                    to   { opacity: 1; transform: rotate(-90deg) translateX(0); }
                }
                @keyframes koza-fade-up {
                    from { opacity: 0; transform: translateY(36px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                .koza-color-bar-anim { opacity: 0; }
                .koza-colors-heading { opacity: 0; }
                .koza-colors-desc    { opacity: 0; }

                .koza-color-bar-anim.koza-bar-in {
                    animation: koza-bar-grow 0.9s cubic-bezier(0.22,1,0.36,1) both;
                }
                .koza-color-bar-anim.koza-bar-in .koza-bar-label {
                    animation: koza-label-fade 0.55s cubic-bezier(0.22,1,0.36,1) both;
                }
                .koza-color-bar-anim:nth-child(1).koza-bar-in                   { animation-delay: 0.1s; }
                .koza-color-bar-anim:nth-child(1).koza-bar-in .koza-bar-label   { animation-delay: 0.75s; }
                .koza-color-bar-anim:nth-child(2).koza-bar-in                   { animation-delay: 0.32s; }
                .koza-color-bar-anim:nth-child(2).koza-bar-in .koza-bar-label   { animation-delay: 0.97s; }
                .koza-color-bar-anim:nth-child(3).koza-bar-in                   { animation-delay: 0.54s; }
                .koza-color-bar-anim:nth-child(3).koza-bar-in .koza-bar-label   { animation-delay: 1.19s; }

                .koza-color-bar-anim {
                    transition: transform 0.35s cubic-bezier(0.22,1,0.36,1),
                                box-shadow 0.35s cubic-bezier(0.22,1,0.36,1);
                }
                .koza-color-bar-anim:hover {
                    transform: scaleX(1.06);
                    box-shadow: 0 12px 40px rgba(12,22,49,0.22);
                    z-index: 2;
                }

                @keyframes koza-screenshot-rise {
                    from { opacity: 0; transform: translateY(70px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .koza-screenshot-anim { opacity: 0; }
                .koza-screenshot-anim.koza-bar-in {
                    animation: koza-screenshot-rise 0.85s cubic-bezier(0.22,1,0.36,1) both;
                }
                .koza-screenshot-anim:nth-child(1).koza-bar-in { animation-delay: 0.05s; }
                .koza-screenshot-anim:nth-child(2).koza-bar-in { animation-delay: 0.22s; }
                .koza-screenshot-anim:nth-child(3).koza-bar-in { animation-delay: 0.39s; }
                .koza-screenshot-anim {
                    transition: transform 0.4s cubic-bezier(0.22,1,0.36,1),
                                box-shadow 0.4s cubic-bezier(0.22,1,0.36,1);
                    border-radius: 12px;
                    overflow: hidden;
                }
                .koza-screenshot-anim:hover {
                    transform: translateY(-12px) scale(1.025);
                    box-shadow: 0 28px 64px rgba(12,22,49,0.18);
                    z-index: 2;
                }

                .koza-colors-heading.koza-text-in {
                    animation: koza-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.05s both;
                }
                .koza-colors-desc.koza-text-in {
                    animation: koza-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.2s both;
                }

                @media (max-width: 768px) {
                    .koza-card { max-width: calc(100% - 32px) !important; border-radius: 40px !important; }
                    .koza-section { padding: 48px 24px !important; }
                    .koza-logo-images { flex-direction: column !important; gap: 20px !important; padding: 0 24px 40px !important; }
                    .koza-logo-img-wrap { width: 100% !important; max-width: 100% !important; height: 120px !important; }
                    .koza-colors-row { flex-direction: column !important; gap: 32px !important; padding: 0 24px 48px !important; }
                    .koza-color-bars { flex-direction: row !important; height: 110px !important; gap: 12px !important; width: 100% !important; flex-shrink: 1 !important; }
                    .koza-color-bar { width: 33.333% !important; flex: 1 1 0 !important; height: 110px !important; }
                    .koza-color-bar-text { transform: rotate(0deg) !important; }
                    .koza-colors-text { width: 100% !important; }
                    .koza-concept { padding: 48px 24px !important; }
                    .koza-screenshots { flex-direction: column !important; align-items: center !important; padding: 40px 24px 60px !important; gap: 24px !important; }
                    .koza-screenshot-wrap { width: 80% !important; max-width: 80% !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>

                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="koza-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 122px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                }}>

                    {/* ── Hero video ── */}
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                        <video
                            src="/videos/koza-hero.mp4"
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                        />
                    </div>

                    {/* ── BRAND LOGO ── top=301, card top=112 → padding-top ~189px */}
                    <div className="koza-section" style={{
                        padding: '100px 80px 50px',
                        boxSizing: 'border-box',
                    }}>
                        <h1 style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 700,
                            fontSize: 'clamp(26px, 4.2vw, 68px)',
                            letterSpacing: '0.1em',
                            color: '#0c1631',
                            margin: '0 0 12px',
                            lineHeight: 1.1,
                        }}>
                            KOZA Aesthetic Care
                            {' '}
                            <span style={{
                                display: 'block',
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 600,
                                fontSize: 'clamp(13px, 1.2vw, 18px)',
                                letterSpacing: '0.08em',
                                color: '#3a4560',
                                marginTop: 12,
                            }}>
                                Brand Identity &amp; Website Design Case Study
                            </span>
                        </h1>
                        <CaseStudyStory
                            problem="KOZA Aesthetic Care is a premium skin, hair and slimming clinic in Bangalore. It needed a brand and website that matched its premium positioning, plus a steady flow of patient enquiries for high-ticket treatments like hair transplants."
                            approach={[
                                'Created the brand identity: logo, colour palette and visual system.',
                                'Designed and built the website, with clear paths from each treatment to booking a consultation.',
                                'Ran treatment-focused Meta lead campaigns, including a dedicated hair transplant campaign.',
                                'Managed KOZA’s Instagram content alongside the paid campaigns.',
                            ]}
                            results={[
                                { num: '726', label: 'Monthly enquiries generated through targeted Meta campaigns', source: 'Meta Ads Manager' },
                                { num: '201', label: 'Qualified treatment enquiries in the first two weeks of Meta campaigns', source: 'Meta Ads Manager' },
                                { num: '21', label: 'Hair transplant leads in three days from a dedicated campaign', source: 'Meta Ads Manager' },
                            ]}
                            services={[
                                { label: 'Brand Strategy', href: '/services/brand-strategy/' },
                                { label: 'Website Development', href: '/services/web-development/' },
                                { label: 'Meta Ads', href: '/services/performance-marketing/meta-ads/' },
                                { label: 'Social Media', href: '/services/social-media/' },
                            ]}
                            theme={{ ink: '#0c1631', body: '#080400', accent: '#b8954f', panel: '#0c1631', panelInk: '#f5fde9', panelAccent: '#e0c58f' }}
                        />
                        <h2 style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 700,
                            fontSize: 'clamp(22px, 3.5vw, 56px)',
                            letterSpacing: '0.13em',
                            color: '#0c1631',
                            margin: 'clamp(40px, 5vw, 72px) 0 20px',
                            lineHeight: 'normal',
                        }}>
                            BRAND LOGO
                        </h2>
                        <div style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            fontWeight: 500,
                            fontSize: 'clamp(13px, 1.1vw, 17px)',
                            color: '#080400',
                            letterSpacing: '0.07em',
                            lineHeight: 1.8,
                            maxWidth: 1320,
                        }}>
                            <p style={{ margin: '0 0 14px' }}>The KOZA brand logo is designed to reflect elegance, sophistication, and a sense of calm. With a refined and minimal approach, the logo captures the essence of premium skincare while maintaining a strong and recognizable presence.</p>
                            <p style={{ margin: '0 0 14px' }}>Its clean structure allows for versatility across digital and physical touchpoints, ensuring consistency throughout the brand experience. The subtle detailing and balanced composition enhance its visual appeal, creating a timeless identity that aligns with the brand's focus on beauty, care, and trust.</p>
                            <p style={{ margin: 0 }}>The logo works seamlessly with the overall visual system, reinforcing a cohesive and professional brand image.</p>
                        </div>
                    </div>

                    {/* ── Two logo images ── Figma: 603×255 and 618×255, gap 88px */}
                    <div className="koza-logo-images" style={{
                        display: 'flex',
                        gap: 'clamp(24px, 5vw, 88px)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 80px 60px',
                        boxSizing: 'border-box',
                    }}>
                        <div className="koza-logo-img-wrap" style={{
                            width: 'clamp(200px, 31vw, 500px)',
                            height: 'clamp(85px, 13vw, 210px)',
                            flexShrink: 0,
                            position: 'relative',
                            overflow: 'hidden',
                            maxWidth: '45%',
                        }}>
                            <Image src="/images/our-work/koza-logo-dark.webp" alt="KOZA logo, navy version"
                                width={500} height={210} style={{ position: 'absolute', objectFit: 'cover', width: '124.68%', height: '147.13%', left: '-13.29%', top: '-23.57%', maxWidth: 'none' }} sizes="45vw" />
                        </div>
                        <div className="koza-logo-img-wrap" style={{
                            width: 'clamp(200px, 32vw, 510px)',
                            height: 'clamp(85px, 13vw, 210px)',
                            flexShrink: 0,
                            position: 'relative',
                            overflow: 'hidden',
                            maxWidth: '45%',
                        }}>
                            <Image src="/images/our-work/koza-logo-gold.webp" alt="KOZA logo, gold version"
                                width={510} height={210} style={{ position: 'absolute', objectFit: 'cover', width: '126.08%', height: '152.48%', left: '-16.39%', top: '-26.91%', maxWidth: 'none' }} sizes="45vw" />
                        </div>
                    </div>

                    {/* ── BRAND COLORS ── Figma: bars 148×927px each, gap 51px + 88px before text, text 955px */}
                    <div className="koza-colors-row" style={{
                        display: 'flex',
                        gap: 'clamp(32px, 5vw, 88px)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 80px 60px',
                        boxSizing: 'border-box',
                    }}>
                        {/* Vertical color bars */}
                        <div className="koza-color-bars" style={{
                            display: 'flex',
                            gap: 'clamp(10px, 2.5vw, 40px)',
                            alignItems: 'stretch',
                            flexShrink: 0,
                            height: 'clamp(280px, 45vw, 700px)',
                        }}>
                            {[
                                { bg: '#0c1631', hex: '#0C1631', textColor: '#fbfdfd', fontWeight: 300 },
                                { bg: '#f5fde9', hex: '#F5FDE9', textColor: '#080400', fontWeight: 400 },
                                { bg: '#e0c58f', hex: '#E0C58F', textColor: '#080400', fontWeight: 400 },
                            ].map(({ bg, hex, textColor, fontWeight }, i) => (
                                <div
                                    key={hex}
                                    ref={(el) => { colorBarRefs.current[i] = el; }}
                                    className="koza-color-bar koza-color-bar-anim"
                                    style={{
                                        background: bg,
                                        width: 'clamp(40px, 7vw, 110px)',
                                        height: '100%',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transformOrigin: 'center center',
                                    }}
                                >
                                    <div className="koza-color-bar-text koza-bar-label" style={{ transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>
                                        <span style={{
                                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                            fontWeight,
                                            fontSize: 'clamp(10px, 1.4vw, 22px)',
                                            color: textColor,
                                            letterSpacing: '0.13em',
                                        }}>{hex}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Brand colors text */}
                        <div className="koza-colors-text" style={{ flex: 1, maxWidth: 760 }}>
                            <h2 ref={colorsHeadingRef} className="koza-colors-heading" style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(20px, 3vw, 48px)',
                                letterSpacing: '0.13em',
                                color: '#0c1631',
                                margin: '0 0 20px',
                                lineHeight: 'normal',
                            }}>
                                BRAND COLORS
                            </h2>
                            <div ref={colorsDescRef} className="koza-colors-desc" style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(12px, 1.1vw, 17px)',
                                color: '#080400',
                                letterSpacing: '0.07em',
                                lineHeight: 1.85,
                            }}>
                                <p style={{ margin: '0 0 12px' }}>The color palette for KOZA is thoughtfully crafted to evoke warmth, luxury, and a sense of calm. A deep, rich brown forms the foundation, adding depth and sophistication, while soft neutral tones bring balance, lightness, and visual clarity to the overall identity.</p>
                                <p style={{ margin: '0 0 12px' }}>This harmonious combination reflects the brand's premium skincare positioning, creating a soothing yet refined aesthetic. The palette enhances visual hierarchy, improves readability, and ensures consistency across the entire website.</p>
                                <p style={{ margin: 0 }}>By blending depth with subtle elegance, the color system elevates the user experience while reinforcing a strong, memorable, and contemporary brand presence.</p>
                            </div>
                        </div>
                    </div>

                    {/* ── WEBSITE CONCEPT dark section ── Figma: bg=#0C1631, 1798×782px */}
                    <div className="koza-concept" style={{
                        background: '#0c1631',
                        width: '100%',
                        padding: 'clamp(48px, 7vw, 100px) clamp(32px, 5vw, 105px)',
                        boxSizing: 'border-box',
                    }}>
                        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
                            <p style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 800,
                                fontSize: 'clamp(11px, 1.2vw, 18px)',
                                color: '#e0c58f',
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                margin: '0 0 20px',
                            }}>
                                WEBSITE CONCEPT
                            </p>
                            <h2 style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(20px, 3.5vw, 56px)',
                                letterSpacing: '0.13em',
                                color: '#f5fde9',
                                margin: '0 0 20px',
                                lineHeight: 'normal',
                            }}>
                                KOZA-AESTHETIC CARE
                            </h2>
                            <div style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 600,
                                fontSize: 'clamp(12px, 1.1vw, 17px)',
                                color: '#e0c58f',
                                letterSpacing: '0.07em',
                                lineHeight: 1.85,
                            }}>
                                <p style={{ margin: '0 0 12px' }}>The KOZA website is designed to deliver a seamless and immersive digital experience that reflects the brand's premium positioning. With a strong focus on clarity, elegance, and usability, the layout guides users effortlessly through services, treatments, and key information.</p>
                                <p style={{ margin: '0 0 12px' }}>A clean visual structure combined with refined typography and a balanced color palette ensures a sophisticated and engaging interface. Each section is thoughtfully crafted to highlight the brand's offerings while maintaining a sense of calm and trust.</p>
                                <p style={{ margin: 0 }}>From intuitive navigation to responsive design, every element works together to create a smooth and consistent experience across all devices. The result is a website that not only showcases the brand effectively but also builds confidence and encourages user interaction.</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Website video ── */}
                    <div style={{
                        width: 'calc(100% - 98px)',
                        margin: '60px auto 0',
                        overflow: 'hidden',
                        borderRadius: 12,
                    }}>
                        <video
                            src="/videos/koza-ourwork-new.mp4"
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </div>

                    {/* ── 3 website screenshots ── Figma: 419×1377, 419×1377, 445×1377, gap 118px */}
                    <div className="koza-screenshots" style={{
                        display: 'flex',
                        gap: 'clamp(16px, 6vw, 100px)',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        padding: 'clamp(40px, 5vw, 80px) clamp(32px, 5vw, 80px) clamp(60px, 6vw, 100px)',
                        boxSizing: 'border-box',
                    }}>
                        {/* Homepage top */}
                        <div
                            ref={(el) => { screenshotRefs.current[0] = el; }}
                            className="koza-screenshot-wrap koza-screenshot-anim"
                            style={{
                                width: 'clamp(100px, 22vw, 360px)',
                                aspectRatio: '419 / 1377',
                                flexShrink: 0,
                                position: 'relative',
                                overflow: 'hidden',
                                maxWidth: '30%',
                            }}
                        >
                            <Image src="/images/our-work/koza-homepage.webp" alt="KOZA homepage design, upper half: hero banner, treatment categories and skin quiz"
                                fill style={{ objectFit: 'cover', objectPosition: 'top' }} sizes="30vw" />
                        </div>
                        {/* Homepage bottom */}
                        <div
                            ref={(el) => { screenshotRefs.current[1] = el; }}
                            className="koza-screenshot-wrap koza-screenshot-anim"
                            style={{
                                width: 'clamp(100px, 22vw, 360px)',
                                aspectRatio: '419 / 1377',
                                flexShrink: 0,
                                position: 'relative',
                                overflow: 'hidden',
                                maxWidth: '30%',
                            }}
                        >
                            <Image src="/images/our-work/koza-homepage.webp" alt="KOZA homepage design, lower half: doctor profiles, offers, FAQs, testimonials and consultation form"
                                fill style={{ objectFit: 'cover', objectPosition: 'bottom' }} sizes="30vw" />
                        </div>
                        {/* Service page */}
                        <div
                            ref={(el) => { screenshotRefs.current[2] = el; }}
                            className="koza-screenshot-wrap koza-screenshot-anim"
                            style={{
                                width: 'clamp(106px, 23vw, 380px)',
                                aspectRatio: '445 / 1377',
                                flexShrink: 0,
                                position: 'relative',
                                overflow: 'hidden',
                                maxWidth: '32%',
                            }}
                        >
                            <Image src="/images/our-work/koza-service.webp" alt="KOZA treatment service page design"
                                fill style={{ objectFit: 'cover', objectPosition: 'top' }} sizes="32vw" />
                        </div>
                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link href="/our-work"
                        style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 16, color: '#fbfdfd', opacity: 0.5, textDecoration: 'none', letterSpacing: '0.05em', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >← Back to Our Work</Link>
                </div>

            </main>
        </div>
    );
}
